import type { Messages } from "../../../core/api";

// Pure value logic of the POC editor. The answer is `POCItemsDocumentAnswer[]` (the shape the
// reused PatientDocumentPOCItem component edits, as the classic modal and mobile store it).
// Only `import type` from core/api so the module loads in node tests.

export type PocAnswer = Messages["POCItemsDocumentAnswer"][];

export interface StoredPocAnswer {
  /** null when nothing is stored (or the stored value is unreadable) */
  items: PocAnswer | null;
  /** false when a stored value exists that is not a POC answer (kept unless the reviewer edits) */
  isReadable: boolean;
}

function isPocItem(value: unknown): value is Messages["POCItemsDocumentAnswer"] {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    typeof record.item === "string" &&
    typeof record.attributes === "object" &&
    record.attributes !== null &&
    typeof (record.attributes as Record<string, unknown>).type === "string"
  );
}

/**
 * The stored answer as POC items. PatientDocumentPOCItem asserts its answer is null or POC items,
 * so anything else is reported unreadable and the editor starts empty.
 */
export function readPocAnswer(value: unknown): StoredPocAnswer {
  if (value === null || value === undefined || value === "") {
    return { items: null, isReadable: true };
  }
  if (Array.isArray(value) && (value as unknown[]).every(isPocItem)) {
    return { items: value as PocAnswer, isReadable: true };
  }
  return { items: null, isReadable: false };
}
